import { useAuth, API } from "../App";
import { 
  ClipboardText, 
  Receipt, 
  Package, 
  GearSix, 
  SignOut,
  List,
  FileArrowDown,
  Calculator,
  Trash,
  ArrowsLeftRight,
  Bell,
  Check,
  CheckCircle,
  X,
  ArrowRight
} from "@phosphor-icons/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { useState, useEffect, useCallback } from "react";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import axios from "axios";

const navItems = [
  { path: "/dashboard", label: "DASHBOARD", icon: ClipboardText },
  { path: "/inventory", label: "INVENTORY", icon: ClipboardText },
  { path: "/waste-log", label: "WASTE LOG", icon: Trash },
  { path: "/transfers", label: "TRANSFERS", icon: ArrowsLeftRight },
  { path: "/invoices", label: "INVOICES", icon: Receipt },
  { path: "/reports", label: "REPORTS", icon: FileArrowDown },
];

const adminItems = [
  { path: "/products", label: "PRODUCTS", icon: Package },
  { path: "/prep-calculator", label: "PREP CALC", icon: Calculator },
  { path: "/settings", label: "SETTINGS", icon: GearSix },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifOpen, setNotifOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(() => {
    const saved = localStorage.getItem('urcogs_zoom');
    return saved ? parseFloat(saved) : 1;
  });

  const handleZoom = (direction) => {
    setZoomLevel(prev => {
      const next = direction === 'in' ? Math.min(prev + 0.1, 1.5) : Math.max(prev - 0.1, 0.7);
      localStorage.setItem('urcogs_zoom', next.toFixed(1));
      return parseFloat(next.toFixed(1));
    });
  };

  const allNavItems = user?.role === "admin" 
    ? [...navItems, ...adminItems] 
    : navItems;

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=20`, { withCredentials: true });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchNotifications();
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(prev => 
        prev.map(n => n.notification_id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.notification_id);
    setNotifOpen(false);
    
    // Navigate based on notification type
    if (notification.type?.includes("transfer")) {
      navigate("/transfers");
    } else if (notification.type?.includes("waste")) {
      navigate("/waste-log");
    }
  };

  const getNotificationIcon = (type) => {
    if (type?.includes("transfer")) return <ArrowsLeftRight size={16} className="text-blue-500" />;
    if (type?.includes("waste")) return <Trash size={16} className="text-orange-500" />;
    return <Bell size={16} className="text-gray-500" />;
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Notification Bell Component
  const NotificationBell = ({ isMobile = false }) => (
    <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="notification-bell"
        >
          <Bell size={isMobile ? 20 : 22} weight={unreadCount > 0 ? "fill" : "regular"} />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-5 w-5 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-medium">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent 
        align="end" 
        className="w-80 p-0"
        data-testid="notification-dropdown"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-heading tracking-wide text-sm">NOTIFICATIONS</h3>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs text-primary"
              onClick={markAllAsRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCircle size={14} className="mr-1" />
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="h-[300px]">
          {notifications.length > 0 ? (
            <div className="divide-y">
              {notifications.map((notif) => (
                <div
                  key={notif.notification_id}
                  className={`px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors ${
                    !notif.read ? "bg-primary/5" : ""
                  }`}
                  onClick={() => handleNotificationClick(notif)}
                  data-testid={`notification-item-${notif.notification_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5">
                      {getNotificationIcon(notif.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${!notif.read ? "font-medium" : ""}`}>
                          {notif.title}
                        </p>
                        {!notif.read && (
                          <span className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {notif.message}
                      </p>
                      <p className="text-xs text-muted-foreground/70 mt-1">
                        {formatTimeAgo(notif.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Bell size={32} className="mb-2 opacity-50" />
              <p className="text-sm">No notifications yet</p>
            </div>
          )}
        </ScrollArea>
        {user?.role === "admin" && (
          <>
            <DropdownMenuSeparator />
            <div className="p-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="w-full justify-between text-xs"
                onClick={() => {
                  setNotifOpen(false);
                  navigate("/settings");
                }}
                data-testid="notification-settings-link"
              >
                Notification Settings
                <ArrowRight size={14} />
              </Button>
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  const NavLinks = ({ onClick }) => (
    <nav className="space-y-1">
      {allNavItems.map((item) => {
        const Icon = item.icon;
        const isActive = location.pathname === item.path;
        return (
          <Link
            key={item.path}
            to={item.path}
            onClick={onClick}
            data-testid={`nav-${item.label.toLowerCase()}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 font-heading tracking-wide text-sm ${
              isActive
                ? "bg-primary text-white"
                : "text-muted-foreground hover:bg-muted hover:text-foreground"
            }`}
          >
            <Icon size={20} weight="duotone" />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#F9F9F7]">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:left-0 lg:flex lg:w-64 lg:flex-col bg-white border-r">
        <div className="flex flex-col flex-1 min-h-0 p-6">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logo.png" alt="URCOGS" className="h-10" />
          </div>
          
          <NavLinks />
          
          <div className="mt-auto pt-6 border-t space-y-3">
            {/* Notification Bell - Desktop */}
            <div className="flex items-center justify-between px-2">
              <span className="text-xs text-muted-foreground font-heading tracking-wide">NOTIFICATIONS</span>
              <NotificationBell />
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-muted transition-colors">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary text-white">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                  <SignOut size={16} className="mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b z-40 px-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                <List size={24} />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-64 p-6">
              <div className="flex items-center gap-3 mb-8">
                <img src="/logo.png" alt="URCOGS" className="h-10" />
              </div>
              <NavLinks onClick={() => setMobileOpen(false)} />
            </SheetContent>
          </Sheet>
          <img src="/logo.png" alt="URCOGS" className="h-8" />
        </div>
        <div className="flex items-center gap-1">
          {/* Notification Bell - Mobile */}
          <NotificationBell isMobile />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary text-white text-sm">
                    {user?.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={logout}>
                <SignOut size={16} className="mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:pl-64 pt-16 lg:pt-0">
        <div className="p-4 lg:p-8" style={{ zoom: zoomLevel }}>{children}</div>
      </main>

      {/* Mobile Zoom Controls */}
      <div className="fixed bottom-4 right-4 flex flex-col gap-1 lg:hidden z-50">
        <button
          onClick={() => handleZoom('in')}
          className="w-9 h-9 rounded-full bg-white border shadow-md flex items-center justify-center text-lg font-bold text-primary active:scale-95 transition-transform"
          data-testid="zoom-in-btn"
        >
          +
        </button>
        <span className="text-[10px] text-center text-muted-foreground font-mono">{Math.round(zoomLevel * 100)}%</span>
        <button
          onClick={() => handleZoom('out')}
          className="w-9 h-9 rounded-full bg-white border shadow-md flex items-center justify-center text-lg font-bold text-primary active:scale-95 transition-transform"
          data-testid="zoom-out-btn"
        >
          -
        </button>
      </div>
    </div>
  );
}
